/**
 * PublicShellFrame — the public top-nav + footer chrome around a page body.
 * PublicShell wraps it for the SPA's public routes; the server-rendered
 * /pub/:slug page renders it directly (signed out, no Convex client).
 */
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './PublicShell.css';

export function PublicShellFrame({
  signedIn, fullPageLinks = false, children,
}: {
  signedIn: boolean;
  /** Links load a full page. Set on /pub, which has no client-side routes. */
  fullPageLinks?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--ink)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PublicNav signedIn={signedIn} reload={fullPageLinks} />
      <div style={{ flex: 1 }}>
        {children}
      </div>
      <PublicFooter reload={fullPageLinks} />
    </div>
  );
}

function PublicNav({ signedIn, reload }: { signedIn: boolean; reload: boolean }) {
  const { pathname } = useLocation();
  // The agent-platform cross-sell only belongs on the marketing
  // landing. Everywhere else this shell renders — /p/:slug above
  // all — it's noise for the visitor's actual errand.
  const onLanding = pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  return (
    <nav className={`public-shell-nav${menuOpen ? ' open' : ''}`}>
      <Link to="/" reloadDocument={reload} className="ps-mark" onClick={closeMenu}>
        <span className="serif ps-wordmark">wydm</span>
        <span className="ps-dot" />
      </Link>
      <button className="ps-menu-toggle" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
        <span /><span /><span />
      </button>
      <div className="ps-links">
        {/* Pricing — the whole app's plans (Free / Premium / Ultra).
            Shown to signed-in + anonymous visitors alike, matching the
            agents-surface nav. */}
        <Link className="ps-link" to="/pricing" reloadDocument={reload} onClick={closeMenu}>Pricing</Link>
        {/* Plain <a>: /blog is server-rendered by Pages Functions, so the
            browser must load it rather than the client router. */}
        <a className="ps-link" href="/blog">Blog</a>
        {onLanding && (
          <Link className="ps-agents" to="/agents" reloadDocument={reload} onClick={closeMenu}>For agents ↗</Link>
        )}
        {signedIn ? (
          /* "How it works" is explained on the landing page itself —
             no nav item needed. Signed-in users only see the pill CTA
             since they don't need a separate Dashboard link. */
          <Link className="ps-cta" to="/home" reloadDocument={reload} onClick={closeMenu}>Go to profile</Link>
        ) : (
          <>
            <Link className="ps-link" to="/auth/signin" reloadDocument={reload} onClick={closeMenu}>Sign in</Link>
            <span className="ps-divider" />
            <Link className="ps-cta" to="/auth/signup" reloadDocument={reload} onClick={closeMenu}>Build my card</Link>
          </>
        )}
      </div>
    </nav>
  );
}

function PublicFooter({ reload }: { reload: boolean }) {
  return (
    <footer
      style={{
        padding: '40px 40px 60px',
        borderTop: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap',
        fontSize: 12,
        color: 'var(--ink-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div className="serif" style={{ fontSize: 18, fontStyle: 'italic', fontWeight: 500 }}>wydm</div>
        <div style={{ width: 4, height: 4, borderRadius: 4, background: 'var(--coral)' }} />
        <span style={{ marginLeft: 6 }}>© 2026 · 18+ only</span>
      </div>
      <div style={{ display: 'flex', gap: 18 }}>
        <a href="/blog" style={{ color: 'inherit', textDecoration: 'none' }}>Blog</a>
        <Link to="/terms" reloadDocument={reload} style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
        <Link to="/privacy" reloadDocument={reload} style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</Link>
        <Link to="/account/delete" reloadDocument={reload} style={{ color: 'inherit', textDecoration: 'none' }}>Delete account</Link>
        <a href="mailto:support@wydm.app" style={{ color: 'inherit', textDecoration: 'none' }}>Contact</a>
      </div>
    </footer>
  );
}
